// src/models/KbArticleCategoryModel.js
// ==========================================
// KbArticleCategoryModel
// Mirrors the `kbArticleCategories` collection document shape.
// One doc per category; articles nested in `articles[]`.
// ==========================================

export class KbArticleCategoryModel {
  static ALLOWED_KEYS = Object.freeze([
    "id",
    "name",
    "audience",
    "companyId",
    "articles",       
    "metadata",
  ]);

  static ALLOWED_METADATA_KEYS = Object.freeze([
    "createdAt",
    "createdBy",
    "updatedAt",
    "updatedBy",
    "deletedFlag",
  ]);

  static ALLOWED_ITEM_KEYS = Object.freeze([
    "id",
    "title",
    "content",
    "tags",
    "metadata",
  ]);

  static ID_FIELD   = "id";
  static NAME_FIELD = "name";

  // ── Item validator ─────────────────────────────────────────────
  static validateItem(item = {}, { isCreation = true } = {}) {
    const errors = [];

    const title   = (item.title   || "").trim();
    const content = (item.content || "").trim();

    if (!title)                    errors.push("title is required");
    if (title && title.length < 5) errors.push("title must be at least 5 characters");

    if (!content)                    errors.push("content is required");
    if (content && content.length < 50)
      errors.push("content must be at least 50 characters");

    if (item.tags !== undefined && !Array.isArray(item.tags))
      errors.push("tags must be an array");

    if (item.id !== undefined && typeof item.id !== "string")
      errors.push("id must be a string");

    return errors.length ? { ok: false, errors } : { ok: true };
  }

  // ── Constructor ────────────────────────────────────────────────
  constructor(data = {}, { isCreation = false } = {}) {
    for (const key of KbArticleCategoryModel.ALLOWED_KEYS) {
      if (key === "metadata") {
        this.metadata = {
          createdAt:   null,
          createdBy:   null,
          updatedAt:   null,
          updatedBy:   null,
          deletedFlag: false,
        };
      } else if (key === "articles") {
        this.articles = [];
      } else if (key === "audience") {
        this.audience = "general";
      } else if (key === "companyId") {
        this.companyId = null;
      } else {
        this[key] = key === "name" ? "" : null;
      }
    }

    if (isCreation) {
      this.id        = data.id        || crypto.randomUUID();
      this.name      = (data.name     || data.title || "").toString();
      this.audience  = data.audience  || "general";
      this.companyId = data.companyId || data.company_id || null;
      this.articles  = Array.isArray(data.articles)
        ? data.articles.map((a) => this._normalizeItem(a, { isCreation: true }))
        : [];

      const now = new Date().toISOString();
      this.metadata = {
        createdAt:   data.metadata?.createdAt   || now,
        createdBy:   data.metadata?.createdBy   || "system",
        updatedAt:   data.metadata?.updatedAt   || now,
        updatedBy:   data.metadata?.updatedBy   || "system",
        deletedFlag: data.metadata?.deletedFlag ?? false,
      };
    } else {
      const source = data.details || data;

      for (const key of Object.keys(source)) {
        if (!KbArticleCategoryModel.ALLOWED_KEYS.includes(key)) continue;

        if (key === "metadata") {
          this.metadata = { ...this.metadata, ...(source.metadata || {}) };
        } else if (key === "articles") {
          this.articles = Array.isArray(source.articles)
            ? source.articles.map((a) => this._normalizeItem(a, { isCreation: false }))
            : [];
        } else {
          this[key] = source[key];
        }
      }

      if (!this.id)   this.id   = data.id || null;
      if (!this.name) this.name = source.name || data.name || "";

      if (data.createdAt) this.metadata.createdAt = data.createdAt;
      if (data.createdBy) this.metadata.createdBy = data.createdBy;
      if (data.updatedAt) this.metadata.updatedAt = data.updatedAt;
      if (data.updatedBy) this.metadata.updatedBy = data.updatedBy;
    }
  }

  _normalizeItem(raw = {}, { isCreation = false } = {}) {
    const out = {};
    for (const k of KbArticleCategoryModel.ALLOWED_ITEM_KEYS) {
      if (k === "metadata") {
        out.metadata = {
          createdAt: raw.createdAt || raw.metadata?.createdAt || null,
          updatedAt: raw.updatedAt || raw.metadata?.updatedAt || null,
        };
      } else if (k === "tags") {
        out.tags = Array.isArray(raw.tags) ? raw.tags : [];
      } else {
        out[k] = raw[k] ?? null;
      }
    }
    if (isCreation) {
      if (!out.id) out.id = `art_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      if (!out.metadata.createdAt) out.metadata.createdAt = new Date().toISOString();
    }
    out.title   = (out.title   || "").toString().trim();
    out.content = (out.content || "").toString().trim();
    return out;
  }

  // ── Validation ─────────────────────────────────────────────────
  isValid() {
    if (!this.id)        return false;
    if (!this.name || this.name.trim().length === 0) return false;
    if (!this.companyId) return false;
    if (!Array.isArray(this.articles)) return false;

    for (const a of this.articles) {
      const { ok } = KbArticleCategoryModel.validateItem(a, { isCreation: false });
      if (!ok) return false;
    }
    return true;
  }

  // ── Payloads ───────────────────────────────────────────────────
  toPayload() {
    const payload = {};
    for (const key of KbArticleCategoryModel.ALLOWED_KEYS) {
      if (key === "metadata") {
        payload.metadata = {};
        for (const mk of KbArticleCategoryModel.ALLOWED_METADATA_KEYS) {
          payload.metadata[mk] =
            this.metadata?.[mk] ?? (mk === "deletedFlag" ? false : null);
        }
      } else if (key === "articles") {
        payload.articles = this.articles.map((a) => ({
          id:      a.id,
          title:   a.title,
          content: a.content,
          tags:    Array.isArray(a.tags) ? a.tags : [],
          metadata: {
            createdAt: a.metadata?.createdAt ?? null,
            updatedAt: a.metadata?.updatedAt ?? null,
          },
        }));
      } else if (this[key] !== undefined) {
        payload[key] = this[key];
      }
    }
    return payload;
  }

  toSupabasePayload() {
    const { id, ...rest } = this.toPayload();
    return { id, details: rest };
  }

  toJSON() {
    return this.toPayload();
  }
}